import type { Entity, ServiceCall } from '../types'

/**
 * Predict the entity state a service call will produce, so the UI can respond
 * instantly. Returns a patched Entity (marked pending) or null when we can't
 * safely predict — in which case we just wait for the real state_changed.
 * The prediction is always reconciled/rolled back against real HA state.
 */
export function predict(call: ServiceCall, current: Entity | undefined): Entity | null {
  if (!current) return null
  const now = Date.now()
  const patch = (over: Partial<Entity>): Entity => ({
    ...current,
    ...over,
    attributes: { ...current.attributes, ...(over.attributes ?? {}) },
    pending: true,
    pendingSince: now,
    last_updated: new Date(now).toISOString(),
  })
  const d = call.data ?? {}

  switch (`${call.domain}.${call.service}`) {
    case 'light.turn_on':
    case 'switch.turn_on':
    case 'input_boolean.turn_on':
    case 'fan.turn_on':
      return patch({
        state: 'on',
        attributes: {
          ...(typeof d.brightness_pct === 'number'
            ? { brightness: Math.round((d.brightness_pct / 100) * 255) }
            : {}),
          ...(typeof d.brightness === 'number' ? { brightness: d.brightness } : {}),
          ...(typeof d.color_temp_kelvin === 'number' ? { color_temp_kelvin: d.color_temp_kelvin } : {}),
        },
      })
    case 'light.turn_off':
    case 'switch.turn_off':
    case 'input_boolean.turn_off':
    case 'fan.turn_off':
      return patch({ state: 'off' })
    case 'light.toggle':
    case 'switch.toggle':
    case 'input_boolean.toggle':
      return patch({ state: current.state === 'on' ? 'off' : 'on' })
    case 'lock.lock':
      return patch({ state: 'locked' })
    case 'lock.unlock':
      return patch({ state: 'unlocked' })
    case 'cover.open_cover':
      return patch({ state: 'open' })
    case 'cover.close_cover':
      return patch({ state: 'closed' })
    case 'climate.set_temperature':
      return typeof d.temperature === 'number'
        ? patch({ attributes: { temperature: d.temperature } })
        : null
    case 'climate.set_hvac_mode':
      return typeof d.hvac_mode === 'string' ? patch({ state: d.hvac_mode }) : null
    case 'media_player.volume_set':
      return typeof d.volume_level === 'number'
        ? patch({ attributes: { volume_level: d.volume_level } })
        : null
    case 'number.set_value':
    case 'input_number.set_value':
      return typeof d.value === 'number' ? patch({ state: String(d.value) }) : null
    case 'input_select.select_option':
    case 'select.select_option':
      return typeof d.option === 'string' ? patch({ state: d.option }) : null
    default:
      return null
  }
}
